"use client";

import { useState } from "react";
import {
  ExclamationTriangleIcon,
  BuildingOfficeIcon,
  BanknotesIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";

const scenarios = [
  {
    id: 1,
    title: "MLS Misrepresentation & Non-Disclosure",
    shortTitle: "Non-Disclosure",
    subtitle: "When Buyers Discover Hidden Problems",
    icon: ExclamationTriangleIcon,
    color: "red",
    situation:
      "A Toronto couple purchased a detached home for $1,450,000 in North York. The MLS listing stated the basement was a legal rental unit generating $1,800/month income. Six months after closing, the City issued a notice that the basement suite was unpermitted and must be decommissioned.",
    problem:
      "The buyers lost expected rental income and faced $85,000 in costs to either legalize or remove the suite. They sued the seller and listing brokerage for misrepresentation and non-disclosure.",
    appraisalRole: [
      "Established property value 'as represented' (with legal rental): $1,450,000",
      "Determined actual value 'as is' (without legal rental): $1,285,000",
      "Quantified damages: $165,000 difference plus remediation costs",
      "Retrospective analysis of comparable legal duplex vs single-family sales",
    ],
    outcome:
      "The litigation appraisal report provided clear, market-supported evidence of the value differential. The case settled before trial with the buyers receiving compensation for their documented losses.",
    keyInsight:
      "Clauses like 'buyer to verify all information' do not eliminate disclosure obligations. A certified appraisal quantifies the actual financial impact of misrepresentation.",
  },
  {
    id: 2,
    title: "Pre-Construction Condo Square Footage Dispute",
    shortTitle: "Builder Dispute",
    subtitle: "When Developers Inflate Unit Sizes",
    icon: BuildingOfficeIcon,
    color: "blue",
    situation:
      "A buyer purchased a pre-construction condo in Toronto's South Core for $1,150,000, marketed as 920 square feet at approximately $1,250/sq ft. Upon taking possession, the buyer discovered the actual interior living space was only 785 square feet—the developer had included the 135 sq ft balcony in the advertised size.",
    problem:
      "The buyer effectively paid $1,465/sq ft for interior space, significantly above market rates. The builder refused to adjust the price, claiming the disclosure was 'industry standard.'",
    appraisalRole: [
      "Analyzed local market rate per sq ft for interior living space: $1,200-$1,350/sq ft",
      "Calculated fair market value based on actual 785 sq ft interior: $980,000-$1,060,000",
      "Documented industry standards showing interior vs. exterior space distinction",
      "Quantified overpayment: approximately $90,000-$170,000",
    ],
    outcome:
      "The expert appraisal report demonstrated the buyer paid a premium far exceeding market rates for the actual interior space received. This evidence supported a damages claim against the developer.",
    keyInsight:
      "In Toronto's high-end condo market ($1,000-$1,800/sq ft range), even small square footage discrepancies can result in six-figure damages. Independent appraisals expose misleading marketing practices.",
  },
  {
    id: 3,
    title: "Failed Transaction & Breach of Contract",
    shortTitle: "Failed Transaction",
    subtitle: "When Buyers Don't Close",
    icon: BanknotesIcon,
    color: "green",
    situation:
      "A seller in Mississauga accepted an offer of $1,380,000 for their detached home. The buyer failed to close after their financing fell through during a market correction. The seller was forced to re-list the property and eventually sold it 5 months later for $1,185,000.",
    problem:
      "The seller sued the original buyer for breach of contract, seeking damages for the $195,000 price difference plus carrying costs, legal fees, and other losses incurred during the extended sale period.",
    appraisalRole: [
      "Established fair market value at original closing date: $1,350,000-$1,380,000",
      "Determined fair market value at eventual re-sale date: $1,175,000-$1,200,000",
      "Documented market conditions and price decline during interim period",
      "Calculated carrying costs attributable to the delay",
    ],
    outcome:
      "The litigation appraisal provided independent verification that the price decline was due to market conditions, not overpricing. This supported the seller's damages claim for losses directly caused by the buyer's breach.",
    keyInsight:
      "Many failed transactions in 2024-2025 involve buyers who didn't obtain pre-purchase appraisals. Sellers now rely on litigation appraisals to prove and quantify their losses.",
  },
];

export default function ScenarioTabs() {
  const [activeScenario, setActiveScenario] = useState(0);
  const scenario = scenarios[activeScenario];

  const colorClasses = {
    red: "from-red-600 to-red-700",
    blue: "from-blue-600 to-blue-700",
    green: "from-green-600 to-green-700",
  };

  return (
    <div className="relative max-w-5xl mx-auto">
      {/* Navigation Tabs */}
      <div className="flex justify-center mb-8">
        <div className="inline-flex flex-wrap justify-center bg-white dark:bg-gray-800 p-1 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 gap-1">
          {scenarios.map((s, index) => (
            <button
              key={index}
              onClick={() => setActiveScenario(index)}
              className={`px-4 py-3 rounded-lg font-semibold transition-all duration-300 text-sm ${
                activeScenario === index
                  ? `bg-gradient-to-r ${colorClasses[s.color]} text-white shadow-md`
                  : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              }`}
            >
              <span className="hidden sm:inline">Scenario {index + 1}: </span>
              {s.shortTitle}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className={`bg-gradient-to-r ${colorClasses[scenario.color]} text-white p-6`}>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-xl">
              <scenario.icon className="w-8 h-8" />
            </div>
            <div>
              <span className="text-sm font-medium opacity-80">Case Study {activeScenario + 1}</span>
              <h3 className="text-2xl font-bold">{scenario.title}</h3>
              <p className="text-white/80">{scenario.subtitle}</p>
            </div>
          </div>
        </div>

        {/* Scenario Content */}
        <div className="p-8">
          <div className="grid md:grid-cols-2 gap-6">
            {/* The Situation */}
            <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-6 border border-gray-200/50 dark:border-gray-600/30">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                <h4 className="text-lg font-bold text-gray-900 dark:text-white">The Situation</h4>
              </div>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{scenario.situation}</p>
            </div>

            {/* The Problem */}
            <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-800/30">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                <h4 className="text-lg font-bold text-gray-900 dark:text-white">The Problem</h4>
              </div>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{scenario.problem}</p>
            </div>
          </div>

          {/* How the Appraisal Helped */}
          <div className="mt-6 bg-gradient-to-br from-blue-50/50 to-green-50/50 dark:from-blue-900/10 dark:to-green-900/10 rounded-xl p-6 border border-gray-200/50 dark:border-gray-600/30">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-3 h-3 bg-green-500 rounded-full"></span>
              <h4 className="text-lg font-bold text-gray-900 dark:text-white">How the Litigation Appraisal Helped</h4>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {scenario.appraisalRole.map((role, i) => (
                <div key={i} className="flex items-start gap-3 bg-white/50 dark:bg-gray-800/50 p-3 rounded-lg">
                  <ShieldCheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 dark:text-gray-300 text-sm">{role}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Outcome & Key Insight */}
          <div className="mt-6 grid md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 p-5 rounded-xl border border-green-200 dark:border-green-800/30">
              <h4 className="text-lg font-bold text-green-800 dark:text-green-300 mb-2">Outcome</h4>
              <p className="text-gray-700 dark:text-gray-300 text-sm">{scenario.outcome}</p>
            </div>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-5 rounded-xl border-l-4 border-yellow-500">
              <h4 className="text-lg font-bold text-yellow-800 dark:text-yellow-300 mb-2">Key Insight</h4>
              <p className="text-gray-700 dark:text-gray-300 text-sm">{scenario.keyInsight}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
